<!-- ✅ UPDATED FILE: ConsultantDashboardStats.vue
     - Revenue card label/desc changes by role (consultant/office = Revenue, client = Expenses)
     - Labels translated via EN/AR keys (provided below)
     - Paid shown AFTER tax, Unpaid shown BEFORE tax for consultant/office
     - Client: Paid/Unpaid are treated as Expenses (still same fields from API, but labeled as expenses)
-->
<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import api from "@/services/api";
import { messages } from "@/i18n";

// ===== lang handling =====
const normalizeLang = (raw) =>
  String(raw || "").toLowerCase().startsWith("en") ? "en" : "ar";

const lang = ref(
  normalizeLang(
    document.documentElement.lang || localStorage.getItem("app_lang") || "ar"
  )
);

const isAr = computed(() => lang.value === "ar");

let observer = null;

// translator — tr()
const getMessage = (key) => {
  const base = messages?.[lang.value];
  if (!base) return null;

  if (base[key] != null) return base[key];
  if (!key.includes(".")) return null;

  return key.split(".").reduce((acc, part) => acc?.[part], base) ?? null;
};

const tr = (key, vars = null) => {
  const val = getMessage(key);
  if (val == null) return key;

  if (vars && typeof val === "string") {
    return val.replace(/\{(\w+)\}/g, (_, k) => (vars?.[k] ?? `{${k}}`));
  }
  return val;
};

// ===== role from cookie (as you said) =====
function getCookie(name) {
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"
    )
  );
  return match ? decodeURIComponent(match[1]) : null;
}

const user = ref(null);

function loadUserFromCookie() {
  const raw = getCookie("auth_user");
  if (!raw) {
    user.value = null;
    return;
  }
  try {
    user.value = JSON.parse(raw);
  } catch (e) {
    console.error("Failed to parse auth_user cookie", e);
    user.value = null;
  }
}

const rawRole = computed(() =>
  String(user.value?.role || user.value?.type || "").toLowerCase()
);

const isConsultantSide = computed(() =>
  ["consultant", "consultant-office"].includes(rawRole.value)
);

const props = defineProps({
  year: { type: Number, default: new Date().getFullYear() },
});

const loading = ref(false);
const dashboard = ref(null);

async function fetchDashboardStats() {
  loading.value = true;
  try {
    const res = await api.get("/dashboard/user/statistics", {
      params: { year: props.year },
    });
    dashboard.value = res?.data ?? null;
  } finally {
    loading.value = false;
  }
}

watch(() => props.year, fetchDashboardStats);

let _langHandler = null;

onMounted(() => {
  loadUserFromCookie();
  fetchDashboardStats();

  _langHandler = (e) => {
    const next = normalizeLang(e?.detail);
    if (next && next !== lang.value) {
      lang.value = next;
    }
  };
  window.addEventListener("app:lang", _langHandler, { passive: true });
  window.addEventListener("app:lang-changed", _langHandler, { passive: true });

  observer = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.attributeName === "lang" || m.attributeName === "dir") {
        lang.value = normalizeLang(document.documentElement.lang || "ar");
      }
    }
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["lang", "dir"],
  });
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
  if (_langHandler) {
    window.removeEventListener("app:lang", _langHandler);
    window.removeEventListener("app:lang-changed", _langHandler);
  }
});

// ===== data mapping =====
const consultationsStats = computed(() => dashboard.value?.consultations_stats ?? {});
const statusStats = computed(() => consultationsStats.value?.status ?? {});
const methodStats = computed(() => consultationsStats.value?.methods ?? {});
const revenueStats = computed(() => consultationsStats.value?.revenue ?? {});
const expensesStats = computed(
  () => consultationsStats.value?.expenses ?? consultationsStats.value?.revenue ?? {}
);

function toNumber(v) {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function formatMoneySAR(n) {
  const num = Number(n ?? 0);
  if (!Number.isFinite(num)) return isAr.value ? "0 ر.س" : "SAR 0";

  if (isAr.value)
    return `${num.toLocaleString("ar-SA", { maximumFractionDigits: 0 })} ر.س`;
  return `SAR ${num.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

// ===== tax handling (consultant/office: paid after tax, unpaid before tax) =====
const TAX_RATE = 0.15;

function addTax(n) {
  const num = Number(n ?? 0);
  if (!Number.isFinite(num)) return 0;
  return num * (1 + TAX_RATE);
}

// API gives:
// - paid_total (consultant: sub_total) -> needs +tax in UI
// - unpaid_total -> show BEFORE tax for consultant-side (as you requested)
const paidDisplayAmount = computed(() => {
  const base = toNumber(
    isConsultantSide.value
      ? revenueStats.value?.paid_total
      : expensesStats.value?.paid_total
  );
  return isConsultantSide.value ? addTax(base) : base;
});

const unpaidDisplayAmount = computed(() => {
  const base = toNumber(
    isConsultantSide.value
      ? revenueStats.value?.unpaid_total
      : expensesStats.value?.unpaid_total
  );
  return base; // before tax (consultant-side) + also OK for client
});

/**
 * ✅ tone -> chart identity colors
 */
function toneClasses(tone) {
  switch (tone) {
    case "teal":
      return "bg-[#e6f6f4] text-[#0f7f74]";
    case "gold":
      return "bg-[#fbf4e6] text-[#c9a23a]";
    case "green":
      return "bg-[#e9fbf1] text-[#166534]";
    case "danger":
      return "bg-[#fdecec] text-[#991b1b]";
    case "primary":
    default:
      return "bg-[var(--primary-degree)] text-[#0f172a]";
  }
}

// ---------------- Consultations (LEFT card)
const consultationItems = computed(() => [
  {
    key: "consult_total",
    value: toNumber(consultationsStats.value?.total),
    label: tr("consultant_dashboard.consultations.total"),
    icon: "mdi mdi-clipboard-text-outline",
    tone: "primary",
  },
  {
    key: "consult_new",
    value: toNumber(statusStats.value?.new),
    label: tr("consultant_dashboard.consultations.status.new"),
    icon: "mdi mdi-star-four-points-outline",
    tone: "teal",
  },
  {
    key: "consult_accepted",
    value: toNumber(statusStats.value?.accepted),
    label: tr("consultant_dashboard.consultations.status.accepted"),
    icon: "mdi mdi-thumb-up-outline",
    tone: "green",
  },
  {
    key: "consult_under",
    value: toNumber(statusStats.value?.under_study),
    label: tr("consultant_dashboard.consultations.status.under_study"),
    icon: "mdi mdi-clock-outline",
    tone: "gold",
  },
  {
    key: "consult_finished",
    value: toNumber(statusStats.value?.finished),
    label: tr("consultant_dashboard.consultations.status.finished"),
    icon: "mdi mdi-check",
    tone: "green",
  },
  {
    key: "consult_methods",
    value: `${toNumber(methodStats.value?.session)} / ${toNumber(
      methodStats.value?.chat
    )}`,
    label: tr("consultant_dashboard.consultations.methods.sessions_chats"),
    icon: "mdi mdi-video-outline",
    tone: "teal",
  },
]);

// ---------------- Revenue/Expenses (RIGHT card) based on role
const moneyCardTitleKey = computed(() =>
  isConsultantSide.value
    ? "consultant_dashboard.money.card_title_revenue"
    : "consultant_dashboard.money.card_title_expenses"
);

const moneyCardDescKey = computed(() =>
  isConsultantSide.value
    ? "consultant_dashboard.money.card_desc_revenue"
    : "consultant_dashboard.money.card_desc_expenses"
);

// Labels for the two rows (paid/unpaid) based on role + tax rule note
const paidLabelKey = computed(() =>
  isConsultantSide.value
    ? "consultant_dashboard.money.paid_after_tax"
    : "consultant_dashboard.money.paid_expenses"
);

const unpaidLabelKey = computed(() =>
  isConsultantSide.value
    ? "consultant_dashboard.money.unpaid_after_tax"
    : "consultant_dashboard.money.unpaid_expenses"
);

const moneyItems = computed(() => [
  {
    key: "money_paid",
    value: formatMoneySAR(paidDisplayAmount.value),
    label: tr(paidLabelKey.value),
    icon: "mdi mdi-cash-check",
    tone: "green",
    pillKey: isConsultantSide.value
      ? "consultant_dashboard.money.pill_received"
      : "consultant_dashboard.money.pill_paid",
    pillTone: isConsultantSide.value ? "success" : "success",
  },
  {
    key: "money_unpaid",
    value: formatMoneySAR(unpaidDisplayAmount.value),
    label: tr(unpaidLabelKey.value),
    icon: "mdi mdi-cash-remove",
    tone: "danger",
    pillKey: isConsultantSide.value
      ? "consultant_dashboard.money.pill_pending"
      : "consultant_dashboard.money.pill_due",
    pillTone: "danger",
  },
]);

const headerRightText = computed(() => {
  if (loading.value) return tr("common.updating");
  return tr("consultant_dashboard.year", { year: props.year });
});
</script>

<template>
  <section :dir="isAr ? 'rtl' : 'ltr'" class="dash space-y-3">
    <!-- ✅ Grid -->
    <div class="grid grid-cols-1 gap-3 lg:grid-cols-12">
      <!-- LEFT: Consultations -->
      <div class="lg:col-span-8 dashCard">
        <div class="dashCard__head">
          <div class="dashCard__head-left">
            <div class="dashCard__icon">
              <i class="mdi mdi-clipboard-text-outline text-[18px]" aria-hidden="true"></i>
            </div>
            <div class="min-w-0">
              <div class="dashCard__title">
                {{ tr("consultant_dashboard.sections.consultations") }}
              </div>
              <div class="dashCard__desc">
                {{ tr("consultant_dashboard.consultations.subtitle") }}
              </div>
            </div>
          </div>

          <div class="dashCard__head-right">
            <span class="dashCard__badge" :title="headerRightText">
              <span v-if="loading" class="dashSkel dashSkel--badge"></span>
              <span v-else>{{ toNumber(consultationsStats?.total) }}</span>
            </span>
          </div>
        </div>

        <div class="dashTiles">
          <div v-for="item in consultationItems" :key="item.key" class="dashTile">
            <div class="dashTile__icon" :class="toneClasses(item.tone)">
              <i :class="item.icon" class="text-[20px] leading-none" aria-hidden="true"></i>
            </div>

            <div class="dashTile__body">
              <div class="dashTile__value">
                <span v-if="loading" class="dashSkel dashSkel--num"></span>
                <span v-else>{{ item.value }}</span>
              </div>
              <div class="dashTile__label">
                <span v-if="loading" class="dashSkel dashSkel--text"></span>
                <span v-else>{{ item.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: Revenue / Expenses -->
      <div class="lg:col-span-4 dashCard">
        <div class="dashCard__head">
          <div class="dashCard__head-left">
            <div class="dashCard__icon">
              <i
                :class="isConsultantSide ? 'mdi mdi-cash-multiple' : 'mdi mdi-cash-minus'"
                class="text-[18px]"
                aria-hidden="true"
              ></i>
            </div>
            <div class="min-w-0">
              <div class="dashCard__title">
                {{ tr(moneyCardTitleKey) }}
              </div>
              <div class="dashCard__desc">
                {{ tr(moneyCardDescKey) }}
              </div>
            </div>
          </div>
        </div>

        <div class="dashList customScroll">
          <div v-for="item in moneyItems" :key="item.key" class="dashRow">
            <div class="dashRow__left">
              <div class="dashRow__icon" :class="toneClasses(item.tone)">
                <i :class="item.icon" class="text-[20px] leading-none" aria-hidden="true"></i>
              </div>

              <div class="dashRow__meta">
                <div class="dashRow__label">
                  <span v-if="loading" class="dashSkel dashSkel--text"></span>
                  <span v-else>{{ item.label }}</span>
                </div>
                <div class="dashRow__value">
                  <span v-if="loading" class="dashSkel dashSkel--num"></span>
                  <span v-else>{{ item.value }}</span>
                </div>
              </div>
            </div>

            <span
              class="dashRow__pill"
              :class="item.pillTone === 'danger' ? 'dashRow__pill--danger' : 'dashRow__pill--success'"
            >
              {{ tr(item.pillKey) }}
            </span>
          </div>

          <!-- <div v-if="!loading && isConsultantSide" class="dashNote">
            {{ tr("consultant_dashboard.money.note_tax") }}
          </div> -->
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.dash {
  --primary-degree: #cdeffb87;
  --brand: var(--sidebar-accent, #1aa3d6);
  --border: #e7eef7;
  --text: #0f172a;
  --muted: #64748b;
}

.dashCard {
  border: 1px solid var(--border);
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.dashCard__head {
  padding: 12px 12px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.dashCard__head-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.dashCard__icon {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: var(--primary-degree);
  border: 1px solid color-mix(in srgb, var(--brand) 18%, #e7eef7);
  color: #0f172a;
  flex: 0 0 auto;
}

.dashCard__title {
  font-size: 13px;
  font-weight: 900;
  color: #0f172a;
}
.dashCard__desc {
  font-size: 12px;
  font-weight: 800;
  color: #64748b;
  opacity: 0.95;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dashCard__badge {
  height: 34px;
  padding: 0 12px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid color-mix(in srgb, var(--brand) 18%, #e7eef7);
  font-weight: 900;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 54px;
}

.dashTiles {
  padding: 12px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
@media (min-width: 768px) {
  .dashTiles {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.dashTile {
  border: 1px solid var(--border);
  border-radius: 16px;
  background: #fff;
  padding: 10px 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: 0.15s ease;
}
.dashTile:hover {
  background: color-mix(in srgb, var(--brand) 4%, #ffffff);
  border-color: color-mix(in srgb, var(--brand) 16%, var(--border));
}

.dashTile__icon,
.dashRow__icon {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(15, 23, 42, 0.06);
  flex: 0 0 auto;
}

.dashTile__body {
  min-width: 0;
}
.dashTile__value {
  font-size: 18px;
  font-weight: 950;
  color: #0f172a;
  line-height: 1.1;
}
.dashTile__label {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 800;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dashList {
  max-height: 260px;
  overflow: auto;
  padding: 10px 12px 12px;
}

.dashRow {
  border: 1px solid var(--border);
  border-radius: 16px;
  background: #fff;
  padding: 10px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}
.dashRow:last-child {
  margin-bottom: 0;
}
.dashRow:hover {
  background: color-mix(in srgb, var(--brand) 4%, #ffffff);
}

.dashRow__left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.dashRow__meta {
  min-width: 0;
}
.dashRow__label {
  font-size: 12px;
  font-weight: 900;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dashRow__value {
  margin-top: 4px;
  font-size: 14px;
  font-weight: 950;
  color: #0f172a;
  white-space: nowrap;
}

.dashRow__pill {
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 950;
  border: 1px solid rgba(15, 23, 42, 0.06);
  white-space: nowrap;
}
.dashRow__pill--success {
  background: #e9fbf1;
  color: #166534;
  border-color: #c9f2db;
}
.dashRow__pill--danger {
  background: #fdecec;
  color: #991b1b;
  border-color: #ffe1e1;
}

.dashNote {
  margin-top: 10px;
  border: 1px dashed color-mix(in srgb, var(--brand) 22%, #e7eef7);
  background: color-mix(in srgb, var(--primary-degree) 55%, #ffffff);
  border-radius: 14px;
  padding: 10px 12px;
  color: #0f172a;
  font-size: 12px;
  font-weight: 800;
}

/* Skeleton */
.dashSkel {
  display: inline-block;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(148, 163, 184, 0.18),
    rgba(148, 163, 184, 0.32),
    rgba(148, 163, 184, 0.18)
  );
  background-size: 200% 100%;
  animation: skel 1.1s infinite linear;
}
.dashSkel--num {
  width: 72px;
  height: 18px;
}
.dashSkel--text {
  width: 140px;
  height: 12px;
  border-radius: 6px;
}
.dashSkel--badge {
  width: 36px;
  height: 14px;
  border-radius: 8px;
}
@keyframes skel {
  0% { background-position: 0% 0%; }
  100% { background-position: 200% 0%; }
}

/* scrollbar */
.customScroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(5, 139, 128, 0.45) transparent;
}
.customScroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.customScroll::-webkit-scrollbar-track {
  background: rgba(5, 139, 128, 0.03);
  border-radius: 999px;
}
.customScroll::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(5, 139, 128, 0.4);
  border: 1.5px solid transparent;
  background-clip: padding-box;
}
.customScroll::-webkit-scrollbar-thumb:hover {
  background: rgba(5, 139, 128, 0.85);
  box-shadow: 0 0 8px rgba(5, 139, 128, 0.4);
}
</style>
